if (!customElements.get('case-selector')) {
  customElements.define(
    'case-selector',
    class CaseSelector extends HTMLElement {
      connectedCallback() {
        const data = this.querySelector('[data-case-data]');
        if (!data) return;

        this.cases = JSON.parse(data.textContent);
        this.strings = JSON.parse(this.querySelector('[data-case-strings]').textContent);
        this.cases.forEach((item) => {
          item.plans = item.plans
            .map((plan, index) => ({ ...plan, days: this.planDays(plan.name, index) }))
            .sort((a, b) => a.days - b.days);
        });

        this.defaultDays = (Number(this.dataset.defaultWeeks) || 0) * 7;
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.form = this.querySelector('product-form form');
        this.variantInput = this.form.querySelector('[name="id"]');
        this.planInput = this.form.querySelector('[data-plan-input]');
        this.submitButton = this.form.querySelector('[type="submit"]');
        this.buttonText = this.submitButton.querySelector('[data-button-text]');
        this.frequency = this.querySelector('[data-frequency]');
        this.live = this.querySelector('[data-live]');

        const checkedCase = this.querySelector('[data-case-input]:checked');
        const checkedPurchase = this.querySelector('[data-purchase-input]:checked');
        const initial = this.caseFromUrl() || this.find(checkedCase?.value) || this.cases[0];

        this.state = {
          block: initial.block,
          purchase: checkedPurchase ? checkedPurchase.value : 'one_time',
          days: this.defaultDays,
        };

        this.querySelectorAll('[data-gallery]').forEach((gallery) => this.setUpGallery(gallery));
        this.setUpStickyBuy();
        this.showOffer();
        this.addEventListener('change', this.onChange.bind(this));
        this.onBlockSelect = this.onBlockSelect.bind(this);
        document.addEventListener('shopify:block:select', this.onBlockSelect);
        this.render(true);
      }

      disconnectedCallback() {
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
        window.removeEventListener('scroll', this.onStickyChange);
        window.removeEventListener('resize', this.onStickyChange);
        this.stickyQuery?.removeEventListener('change', this.onStickyChange);
      }

      showOffer() {
        const offer = this.querySelector('[data-offer]');
        const data = this.querySelector('[data-offer-data]');
        if (!offer || !data) return;

        const banners = JSON.parse(data.textContent);
        const code = (new URLSearchParams(window.location.search).get('discount') || '').toUpperCase().trim();
        const banner = banners[code] || (window.Shopify?.designMode ? Object.values(banners)[0] : null);
        if (!banner) return;

        offer.innerHTML = banner;
        offer.hidden = false;
      }

      setUpStickyBuy() {
        this.buySlot = this.querySelector('[data-buy-slot]');
        this.packStep = this.querySelector('[data-pack-step]');
        if (!this.buySlot || !this.packStep) return;

        this.stickyQuery = window.matchMedia('(max-width: 989px)');
        let frame;
        this.onStickyChange = () => {
          cancelAnimationFrame(frame);
          frame = requestAnimationFrame(() => this.updateStickyBuy());
        };
        window.addEventListener('scroll', this.onStickyChange, { passive: true });
        window.addEventListener('resize', this.onStickyChange, { passive: true });
        this.stickyQuery.addEventListener('change', this.onStickyChange);
        this.updateStickyBuy();
      }

      updateStickyBuy() {
        const pack = this.packStep.getBoundingClientRect();
        const slot = this.buySlot.getBoundingClientRect();
        const slotOnScreen = slot.top >= 0 && slot.bottom <= window.innerHeight;
        const pinned = this.stickyQuery.matches && pack.bottom < 0 && !slotOnScreen;
        if (this.buySlot.classList.contains('is-pinned') === pinned) return;
        this.buySlot.style.height = pinned ? `${this.buySlot.offsetHeight}px` : '';
        this.buySlot.classList.toggle('is-pinned', pinned);
      }

      find(blockId) {
        return this.cases.find((item) => item.block === blockId);
      }

      current() {
        return this.find(this.state.block) || this.cases[0];
      }

      planDays(name, index) {
        const match = String(name).match(/(\d+)\s*(day|week|month|year)/i);
        if (!match) return 1000 + index;
        const unit = { day: 1, week: 7, month: 30, year: 365 }[match[2].toLowerCase()];
        return Number(match[1]) * unit;
      }

      caseFromUrl() {
        const variant = Number(new URLSearchParams(window.location.search).get('variant'));
        if (!variant) return null;
        return this.cases.find((item) => item.variant === variant && item.available) || null;
      }

      onBlockSelect(event) {
        if (!this.contains(event.target)) return;
        const item = this.find(event.target.dataset.caseItem);
        if (!item) return;
        this.state.block = item.block;
        this.render();
      }

      onChange(event) {
        const target = event.target;
        const current = this.current();

        if (target.matches('[data-pack-input]')) {
          this.state.block = this.pickCase(Number(target.value), current.type, current.product).block;
        } else if (target.matches('[data-type-input]')) {
          this.state.block = this.pickType(current.cans, target.value).block;
        } else if (target.matches('[data-case-input]')) {
          this.state.block = target.value;
        } else if (target.matches('[data-purchase-input]')) {
          this.state.purchase = target.value;
        } else if (target.matches('[data-frequency]')) {
          const plan = current.plans.find((item) => String(item.id) === target.value);
          if (plan) this.state.days = plan.days;
        } else {
          return;
        }

        this.render();
      }

      pickCase(cans, type, product) {
        const sized = this.cases.filter((item) => item.cans === cans);
        const available = sized.filter((item) => item.available);
        return (
          available.find((item) => item.type === type && item.product === product) ||
          available.find((item) => item.type === type) ||
          available[0] ||
          sized[0] ||
          this.current()
        );
      }

      pickType(cans, type) {
        const sizes = [...new Set(this.cases.filter((item) => item.type === type).map((item) => item.cans))];
        if (!sizes.length || sizes.includes(cans)) return this.pickCase(cans, type);
        const inStock = sizes.filter((size) =>
          this.cases.some((item) => item.cans === size && item.type === type && item.available)
        );
        const nearest = (inStock.length ? inStock : sizes).reduce((best, size) =>
          Math.abs(size - cans) < Math.abs(best - cans) ? size : best
        );
        return this.pickCase(nearest, type);
      }

      planFor(item) {
        if (!item.plans.length) return null;
        const exact = (days) => item.plans.find((plan) => plan.days === days);
        const nearest = item.plans.reduce((best, plan) =>
          Math.abs(plan.days - this.state.days) < Math.abs(best.days - this.state.days) ? plan : best
        );
        return exact(this.state.days) || exact(this.defaultDays) || nearest;
      }

      purchaseFor(item) {
        if (item.requiresPlan && item.plans.length) return 'subscribe';
        if (!item.plans.length) return 'one_time';
        return this.state.purchase;
      }

      priceFor(item) {
        const plan = this.purchaseFor(item) === 'subscribe' ? this.planFor(item) : null;
        return plan ? { cents: plan.price, text: plan.priceText } : { cents: item.price, text: item.priceText };
      }

      lowestPrice(cans, purchase) {
        return this.cases
          .filter((item) => item.cans === cans && item.available)
          .flatMap((item) =>
            purchase === 'subscribe' && item.plans.length ? item.plans : [{ price: item.price, priceText: item.priceText }]
          )
          .reduce((lowest, entry) => (!lowest || entry.price < lowest.price ? entry : lowest), null);
      }

      render(initial = false) {
        const current = this.current();
        const purchase = this.purchaseFor(current);
        const plan = this.planFor(current);
        const price = this.priceFor(current);

        this.renderPacks(current, purchase);
        this.renderTypes(current);
        this.renderCases(current);
        this.renderPurchase(current, purchase, plan);
        this.renderForm(current, purchase, plan, price);
        this.showProduct(current, initial);

        if (!initial) {
          this.live.textContent = [current.title, current.pack, current.available ? price.text : this.strings.soldOut].join(', ');
          this.updateUrl(current.variant);
        }
      }

      renderPacks(current, purchase) {
        this.querySelectorAll('[data-pack-input]').forEach((input) => {
          const cans = Number(input.value);
          const label = input.parentElement.querySelector('[data-pack-price]');
          const lowest = this.lowestPrice(cans, purchase);
          input.checked = cans === current.cans;
          input.disabled = !lowest;
          if (lowest) {
            label.textContent = this.strings.from.replace('[price]', lowest.priceText);
          } else {
            label.textContent = this.strings.soldOut;
          }
        });
      }

      renderTypes(current) {
        this.querySelectorAll('[data-type-input]').forEach((input) => {
          input.checked = input.value === current.type;
          input.toggleAttribute(
            'data-unavailable',
            !this.cases.some((item) => item.cans === current.cans && item.type === input.value)
          );
        });
      }

      renderCases(current) {
        this.cases.forEach((item) => {
          const element = this.querySelector(`[data-case-item="${item.block}"]`);
          if (!element) return;
          element.hidden = item.cans !== current.cans || item.type !== current.type;
          const input = element.querySelector('[data-case-input]');
          input.checked = item.block === current.block;
        });
      }

      renderPurchase(current, purchase, plan) {
        const subscribeOption = this.querySelector('[data-subscribe-option]');
        const oneTimeOption = this.querySelector('[data-one-time-option]');

        subscribeOption.hidden = !current.plans.length;
        oneTimeOption.hidden = current.requiresPlan && current.plans.length > 0;
        subscribeOption.classList.toggle('is-selected', purchase === 'subscribe');
        oneTimeOption.classList.toggle('is-selected', purchase === 'one_time');
        this.querySelector('[data-subscribe-body]').hidden = purchase !== 'subscribe';
        const oneTimeBody = this.querySelector('[data-one-time-body]');
        if (oneTimeBody) oneTimeBody.hidden = purchase !== 'one_time';
        this.querySelectorAll('[data-purchase-input]').forEach((input) => {
          input.checked = input.value === purchase;
        });

        this.querySelector('[data-one-time-price]').textContent = current.priceText;
        this.querySelector('[data-subscribe-price]').textContent = plan ? plan.priceText : '';
        this.querySelector('[data-save]').textContent =
          plan && plan.save > 0 ? this.strings.save.replace('[percent]', plan.save) : '';

        this.frequency.replaceChildren(
          ...current.plans.map((entry) => {
            const option = document.createElement('option');
            option.value = entry.id;
            option.textContent = entry.name;
            option.selected = plan && entry.id === plan.id;
            return option;
          })
        );
      }

      renderForm(current, purchase, plan, price) {
        this.variantInput.value = current.variant;
        this.planInput.value = plan ? plan.id : '';
        this.planInput.disabled = purchase !== 'subscribe' || !plan;

        this.querySelector('.product-form__error-message-wrapper')?.setAttribute('hidden', '');
        this.submitButton.removeAttribute('aria-disabled');
        this.submitButton.querySelector('.sold-out-message')?.classList.add('hidden');
        this.buttonText.classList.remove('hidden');
        this.submitButton.disabled = !current.available;
        this.buttonText.textContent = current.available ? `${this.strings.add} - ${price.text}` : this.strings.soldOut;
      }

      showProduct(current, initial) {
        const title = this.querySelector('[data-product-title]');
        if (title) title.textContent = current.productTitle;
        this.querySelector('[data-summary-title]').textContent = current.productTitle;
        const meta = this.querySelector('[data-summary-meta]');
        if (meta) meta.textContent = this.strings.summary.replace('[cans]', current.cans);
        const highlights = this.querySelector('[data-summary-highlights]');
        highlights.textContent = current.highlights;
        highlights.hidden = !current.highlights;

        this.querySelectorAll('[data-details]').forEach((panel) => {
          panel.hidden = Number(panel.dataset.details) !== current.product;
        });

        this.querySelectorAll('[data-gallery]').forEach((gallery) => {
          const active = Number(gallery.dataset.gallery) === current.product;
          const lead = active ? String(current.media || gallery.order[0]) : null;
          const changed = gallery.hidden === active || (active && gallery.lead !== lead);
          gallery.hidden = !active;
          if (!active || !changed) return;
          gallery.lead = lead;
          this.orderGallery(gallery, lead);
          if (!initial || gallery.track.scrollLeft !== 0) gallery.track.scrollLeft = 0;
          this.syncThumbs(gallery);
        });
      }

      setUpGallery(gallery) {
        gallery.track = gallery.querySelector('[data-slides]');
        gallery.thumbs = gallery.querySelector('[data-thumbs]');
        gallery.order = Array.from(gallery.querySelectorAll('[data-slide]'), (slide) => slide.dataset.slide);

        gallery.querySelector('[data-prev]')?.addEventListener('click', () => this.step(gallery, -1));
        gallery.querySelector('[data-next]')?.addEventListener('click', () => this.step(gallery, 1));
        gallery.thumbs?.addEventListener('click', (event) => {
          const thumb = event.target.closest('[data-thumb]');
          if (thumb) this.goTo(gallery, gallery.track.querySelector(`[data-slide="${thumb.dataset.thumb}"]`));
        });

        let frame;
        gallery.track.addEventListener(
          'scroll',
          () => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => this.syncThumbs(gallery));
          },
          { passive: true }
        );
      }

      orderGallery(gallery, lead) {
        const ids = [lead, ...gallery.order.filter((id) => id !== lead)];
        const label = gallery.dataset.thumbLabel;
        ids.forEach((id, index) => {
          const slide = gallery.track.querySelector(`[data-slide="${id}"]`);
          if (slide) gallery.track.append(slide);
          const item = gallery.thumbs?.querySelector(`[data-thumb-item="${id}"]`);
          if (!item) return;
          gallery.thumbs.append(item);
          item.querySelector('[data-thumb]').setAttribute('aria-label', label.replace('[index]', index + 1));
        });
      }

      slides(gallery) {
        return Array.from(gallery.track.querySelectorAll('[data-slide]'));
      }

      activeIndex(gallery) {
        const width = gallery.track.clientWidth || 1;
        return Math.round(gallery.track.scrollLeft / width);
      }

      step(gallery, direction) {
        const slides = this.slides(gallery);
        const next = (this.activeIndex(gallery) + direction + slides.length) % slides.length;
        this.goTo(gallery, slides[next]);
      }

      goTo(gallery, slide) {
        if (!slide) return;
        gallery.track.scrollTo({
          left: slide.offsetLeft,
          behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
        });
      }

      syncThumbs(gallery) {
        if (!gallery.thumbs) return;
        const index = this.activeIndex(gallery);
        gallery.thumbs.querySelectorAll('[data-thumb]').forEach((thumb, position) => {
          thumb.setAttribute('aria-current', position === index ? 'true' : 'false');
        });
      }

      updateUrl(variant) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant);
        window.history.replaceState({}, '', url.toString());
      }
    }
  );
}
